"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, Flame, Salad, ChefHat, UtensilsCrossed } from "lucide-react";

type StationKey = "grill" | "fryer" | "salad" | "expo";

const TICKET_ITEMS: Record<StationKey, { name: { en: string; es: string }; qty: number }[]> = {
  grill: [
    { name: { en: "Carne Asada", es: "Carne Asada" }, qty: 2 },
    { name: { en: "Birria Plate", es: "Plato Birria" }, qty: 1 },
  ],
  fryer: [
    { name: { en: "Yuca Fries", es: "Yuca Fries" }, qty: 1 },
  ],
  salad: [
    { name: { en: "Pico de Gallo", es: "Pico de Gallo" }, qty: 2 },
  ],
  expo: [],
};

// Bump timing in seconds (relative to cycle start)
const BUMP_AT: Record<Exclude<StationKey, "expo">, number> = {
  grill: 1500,
  salad: 2900,
  fryer: 4400,
};

const CYCLE_MS = 7500;

export default function KitchenDisplayChoreography() {
  const { t } = useLang();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      setTick((now - start) % CYCLE_MS);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const grillDone = tick >= BUMP_AT.grill;
  const saladDone = tick >= BUMP_AT.salad;
  const fryerDone = tick >= BUMP_AT.fryer;
  const expoReady = grillDone && saladDone && fryerDone;

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[800px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("One ticket. Four cooks.", "Un ticket. Cuatro cocineros.")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("The grill sees grill. ", "El grill ve grill. ")}
            <span className="text-redy-charcoal/30">
              {t("The fryer sees fryer.", "La freidora ve freidora.")}
            </span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Order #142 fans out across stations. Each cook bumps only what they own. Expo turns green when the whole plate is ready. No one is staring at a ticket they can't act on.",
              "La orden #142 se reparte en las estaciones. Cada cocinero hace bump solo de lo suyo. Expo se pone verde cuando el plato entero está listo. Nadie mira un ticket que no le toca."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative max-w-[960px] mx-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <StationPanel
              station="grill"
              label={t("Grill", "Grill")}
              icon={<Flame className="w-3.5 h-3.5" />}
              items={TICKET_ITEMS.grill}
              done={grillDone}
              tick={tick}
              bumpAt={BUMP_AT.grill}
            />
            <StationPanel
              station="fryer"
              label={t("Fryer", "Freidora")}
              icon={<UtensilsCrossed className="w-3.5 h-3.5" />}
              items={TICKET_ITEMS.fryer}
              done={fryerDone}
              tick={tick}
              bumpAt={BUMP_AT.fryer}
            />
            <StationPanel
              station="salad"
              label={t("Salad", "Salads")}
              icon={<Salad className="w-3.5 h-3.5" />}
              items={TICKET_ITEMS.salad}
              done={saladDone}
              tick={tick}
              bumpAt={BUMP_AT.salad}
            />
            <ExpoPanel
              label={t("Expo", "Expo")}
              icon={<ChefHat className="w-3.5 h-3.5" />}
              grillDone={grillDone}
              saladDone={saladDone}
              fryerDone={fryerDone}
              ready={expoReady}
            />
          </div>

          <AnimatePresence>
            {expoReady && (
              <motion.div
                key="ready-banner"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4 }}
                className="absolute -bottom-6 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-full text-[12px] font-black uppercase tracking-widest shadow-lg whitespace-nowrap"
                style={{ backgroundColor: "#2ECC71", color: "white" }}
              >
                {t("Plate ready · Call it", "Plato listo · Cántalo")}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-[14px] font-medium text-redy-secondary mt-16 max-w-[640px] mx-auto"
        >
          {t(
            "Coordination without yelling. Without a paper ticket spike. Without 'where's my fries?' every Saturday night.",
            "Coordinación sin gritar. Sin pinchador de tickets. Sin '¿dónde están las papas?' cada sábado noche."
          )}
        </motion.p>
      </div>
    </section>
  );
}

function StationPanel({
  station,
  label,
  icon,
  items,
  done,
  tick,
  bumpAt,
}: {
  station: StationKey;
  label: string;
  icon: React.ReactNode;
  items: { name: { en: string; es: string }; qty: number }[];
  done: boolean;
  tick: number;
  bumpAt: number;
}) {
  const { t } = useLang();
  const justBumped = done && tick - bumpAt < 600;
  const elapsedSec = Math.max(0, Math.min(99, Math.floor(tick / 1000)));

  return (
    <div className="bg-redy-charcoal rounded-[20px] p-5 shadow-[0_20px_50px_rgba(0,0,0,0.18)] relative overflow-hidden">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-white/70">
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center"
            style={{ backgroundColor: "rgba(244, 114, 43, 0.18)", color: "#F4722B" }}
          >
            {icon}
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#F4722B" }} />
          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#F4722B" }}>
            Live
          </span>
        </div>
      </div>

      <motion.div
        animate={{
          opacity: done ? 0.45 : 1,
          scale: justBumped ? [1, 1.02, 1] : 1,
        }}
        transition={{ duration: justBumped ? 0.5 : 0.3 }}
        className="rounded-[12px] p-3.5"
        style={{
          backgroundColor: done ? "rgba(46, 204, 113, 0.08)" : "rgba(244, 114, 43, 0.08)",
          border: `1px solid ${done ? "rgba(46, 204, 113, 0.35)" : "rgba(244, 114, 43, 0.35)"}`,
        }}
      >
        <div className="flex justify-between items-baseline mb-2">
          <div className="text-[14px] font-black text-white">#142</div>
          <div className="flex items-center gap-1.5">
            <div
              className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded"
              style={{
                backgroundColor: done ? "#2ECC71" : "#F4722B",
                color: "white",
              }}
            >
              {done ? t("Done", "Listo") : t("Fire", "Cocinar")}
            </div>
          </div>
        </div>

        <div className="space-y-1 text-[11px] text-white/85">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div
                className="w-3.5 h-3.5 rounded-sm flex items-center justify-center text-white shrink-0"
                style={{ backgroundColor: done ? "#2ECC71" : "rgba(255,255,255,0.15)" }}
              >
                {done && <Check className="w-2.5 h-2.5" strokeWidth={3.5} />}
              </div>
              <span className="font-bold">{item.qty}×</span>
              <span>{t(item.name.en, item.name.es)}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 pt-2 border-t border-white/10 flex items-center justify-between">
          <div className="text-[9px] font-bold text-white/50 tabular-nums">
            T+{elapsedSec.toString().padStart(2, "0")}s
          </div>
          <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: done ? "#2ECC71" : "#F4722B" }}>
            {done ? t("Bumped", "Bumpeado") : t("In progress", "En progreso")}
          </div>
        </div>
      </motion.div>

      {/* Station-only badge */}
      <div className="absolute top-3 right-3 hidden">
        {station}
      </div>
    </div>
  );
}

function ExpoPanel({
  label,
  icon,
  grillDone,
  saladDone,
  fryerDone,
  ready,
}: {
  label: string;
  icon: React.ReactNode;
  grillDone: boolean;
  saladDone: boolean;
  fryerDone: boolean;
  ready: boolean;
}) {
  const { t } = useLang();
  const stations = [
    { key: "grill", label: t("Grill", "Grill"), done: grillDone },
    { key: "fryer", label: t("Fryer", "Freidora"), done: fryerDone },
    { key: "salad", label: t("Salad", "Salads"), done: saladDone },
  ];

  return (
    <div className="bg-redy-charcoal rounded-[20px] p-5 shadow-[0_20px_50px_rgba(0,0,0,0.18)] relative overflow-hidden">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-white/70">
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center"
            style={{
              backgroundColor: ready ? "rgba(46, 204, 113, 0.22)" : "rgba(215, 38, 61, 0.18)",
              color: ready ? "#2ECC71" : "#D7263D",
            }}
          >
            {icon}
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
        </div>
        <div className="text-[9px] font-bold uppercase tracking-wider text-white/40">
          {t("Sees all", "Ve todo")}
        </div>
      </div>

      <motion.div
        animate={{
          backgroundColor: ready ? "rgba(46, 204, 113, 0.12)" : "rgba(215, 38, 61, 0.08)",
          borderColor: ready ? "rgba(46, 204, 113, 0.5)" : "rgba(215, 38, 61, 0.4)",
        }}
        transition={{ duration: 0.5 }}
        className="rounded-[12px] p-3.5 border"
      >
        <div className="flex justify-between items-baseline mb-3">
          <div className="text-[14px] font-black text-white">#142</div>
          <motion.div
            animate={{
              backgroundColor: ready ? "#2ECC71" : "#D7263D",
              scale: ready ? [1, 1.08, 1] : 1,
            }}
            transition={{ duration: 0.4 }}
            className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded text-white"
          >
            {ready ? t("Ready", "Listo") : t("Working", "Trabajando")}
          </motion.div>
        </div>

        <div className="space-y-1.5">
          {stations.map((s) => (
            <div key={s.key} className="flex items-center justify-between text-[11px]">
              <div className="flex items-center gap-2 text-white/85">
                <div
                  className="w-3.5 h-3.5 rounded-sm flex items-center justify-center text-white shrink-0"
                  style={{ backgroundColor: s.done ? "#2ECC71" : "rgba(255,255,255,0.15)" }}
                >
                  {s.done && <Check className="w-2.5 h-2.5" strokeWidth={3.5} />}
                </div>
                <span className="font-bold uppercase tracking-wider text-[10px]">{s.label}</span>
              </div>
              <div
                className="text-[9px] font-bold uppercase tracking-wider"
                style={{ color: s.done ? "#2ECC71" : "#F4722B" }}
              >
                {s.done ? t("Done", "Listo") : t("Pending", "Pendiente")}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 pt-2 border-t border-white/10 text-center">
          <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: ready ? "#2ECC71" : "#F4722B" }}>
            {ready
              ? t("All stations bumped · Plate it", "Todas las estaciones · Empaca")
              : t("Waiting on the line", "Esperando a la línea")}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
